import { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Dialog, DialogContent } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { EEOccupationalLevel, SeverityLevel, DemographicGroup } from '../../../../Component/Model/Enum/EmploymentEquity';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { WorkForeProfileDrilldown } from '../../Plan/WorkforceProfile/Drilldown/WorkForceProfileDrillDown';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { PageTitle } from '../../../../Component/Common/Components/PageTitle';
import { FilterBar, FilterValues } from '../../../../Component/Common/Components/FilterBar/FilterBar';
import { IFilter, IFilterValue, FilterType } from '../../../../Component/Common/Components/DataTable/Filters';
import { ScorecardAtom } from '../../../../State/Scorecard.Atom';
import { DeviationsService } from '../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { HeatMapGrid } from './HeatMapGrid';
import { HeatMapToolbar, CellContent, DesignationFilter } from './HeatMapToolbar';
import { SeverityStatCards } from './SeverityStatCards';
import {
    HeatMapCellData,
    designatedDemographics,
    occupationalLevels,
    occupationalLevelDisplayNames,
    demographicGroups,
    mapCellsFromDto,
} from './deviationsSummaryHelper';

const occupationalLevelOptions: IFilterValue[] = occupationalLevels.map(l => ({
    key: occupationalLevelDisplayNames[l],
    val: l,
}));
const demographicOptions: IFilterValue[] = demographicGroups.map(d => ({ key: d, val: d }));
const severityOptions: IFilterValue[] = [
    { key: 'Critical',         val: SeverityLevel.Critical },
    { key: 'High',             val: SeverityLevel.High },
    { key: 'Medium',           val: SeverityLevel.Medium },
    { key: 'On Track',         val: SeverityLevel.OnTrack },
    { key: 'Over-represented', val: SeverityLevel.OverRepresented },
];

const filters: IFilter[] = [
    { label: 'Occupational Level', propname: 'occupationalLevel', type: FilterType.multiselect, values: occupationalLevelOptions },
    { label: 'Demographic',        propname: 'demographic',        type: FilterType.multiselect, values: demographicOptions },
    { label: 'Severity',           propname: 'severity',           type: FilterType.multiselect, values: severityOptions },
];

export function DeviationsSummaryPage() {
    const scorecard = useRecoilValue(ScorecardAtom);

    const [cells, setCells] = useState<HeatMapCellData[]>([]);
    const [designatedDisabled, setDesignatedDisabled] = useState<DeviationsDto.HeatmapDesignatedDisabledDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [reportDateTo, setReportDateTo] = useState<Date | undefined>(undefined);
    const [drilldownCell, setDrilldownCell] = useState<HeatMapCellData | null>(null);

    const [cellContent, setCellContent] = useState<CellContent>('detailed');
    const [designationFilter, setDesignationFilter] = useState<DesignationFilter>('all');
    const [filterValues, setFilterValues] = useState<FilterValues>({
        occupationalLevel: [],
        demographic: [],
        severity: [],
    });

    useEffect(() => {
        if (!scorecard.scorecardId) return;
        setLoading(true);
        setError(null);
        DeviationsService.GetHeatmapSummary(scorecard.scorecardId)
            .then(res => {
                setCells(mapCellsFromDto(res.data.cells));
                setDesignatedDisabled(res.data.designatedDisabled);
                setReportDateTo(new Date(res.data.reportDateTo));
            })
            .catch(() => setError('Failed to load heatmap data.'))
            .finally(() => setLoading(false));
    }, [scorecard.scorecardId]);

    const visibleDemographics = designationFilter === 'designated' ? designatedDemographics : undefined;

    const visibleCells = useMemo(() => {
        return visibleDemographics
            ? cells.filter(c => visibleDemographics.includes(c.demographicGroup))
            : cells;
    }, [cells, visibleDemographics]);

    const statistics = useMemo(() => ({
        [SeverityLevel.Critical]:        visibleCells.filter(c => c.severity === SeverityLevel.Critical).length,
        [SeverityLevel.High]:            visibleCells.filter(c => c.severity === SeverityLevel.High).length,
        [SeverityLevel.Medium]:          visibleCells.filter(c => c.severity === SeverityLevel.Medium).length,
        [SeverityLevel.OnTrack]:         visibleCells.filter(c => c.severity === SeverityLevel.OnTrack).length,
        [SeverityLevel.OverRepresented]: visibleCells.filter(c => c.severity === SeverityLevel.OverRepresented).length,
    }), [visibleCells]);

    const selectedLevels = useMemo(
        () => new Set((filterValues.occupationalLevel as IFilterValue[]).map(v => v.val as EEOccupationalLevel)),
        [filterValues],
    );
    const selectedDemographics = useMemo(
        () => new Set((filterValues.demographic as IFilterValue[]).map(v => v.val as DemographicGroup)),
        [filterValues],
    );
    const selectedSeverities = useMemo(
        () => new Set((filterValues.severity as IFilterValue[]).map(v => v.val as SeverityLevel)),
        [filterValues],
    );

    return (
        <MDBox style={{ padding: '10px 0' }}>
            <PageTitle title='Deviations - Summary' />

            <Card sx={{ borderRadius: '10px' }}>
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <HeatMapToolbar
                        cellContent={cellContent}
                        onCellContentChange={setCellContent}
                        designationFilter={designationFilter}
                        onDesignationChange={setDesignationFilter}
                    />

                    <SeverityStatCards statistics={statistics} />

                    <Box sx={{ mb: 3 }}>
                        <FilterBar
                            filters={filters}
                            onChange={setFilterValues}
                            hideClearFilters={false}
                            disableCard
                        />
                    </Box>

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'error.main', fontSize: '0.875rem' }}>
                            {error}
                        </Box>
                    )}

                    {!loading && !error && (
                        <HeatMapGrid
                            data={visibleCells}
                            selectedSeverities={selectedSeverities}
                            selectedLevels={selectedLevels}
                            selectedDemographicGroups={selectedDemographics}
                            visibleDemographics={visibleDemographics}
                            designatedDisabled={designatedDisabled}
                            hidePercentages={cellContent !== 'detailed'}
                            hideTarget={false}
                            cellWidth={130}
                            showHeadcounts
                            onCellClick={setDrilldownCell}
                        />
                    )}

                    <Dialog
                        open={drilldownCell !== null}
                        onClose={() => setDrilldownCell(null)}
                        aria-labelledby="deviations-drilldown-dialog"
                        maxWidth={false}
                        sx={{ minWidth: '1200px' }}
                    >
                        <CustomDialogTitle id="deviations-drilldown-dialog" onClose={() => setDrilldownCell(null)}>
                            Employee Structure
                        </CustomDialogTitle>
                        <DialogContent>
                            {drilldownCell && (
                                <WorkForeProfileDrilldown
                                    scorecardId={scorecard.scorecardId}
                                    race={drilldownCell.race}
                                    gender={drilldownCell.gender}
                                    occupationalLevel={drilldownCell.occupationalLevel}
                                    isDisabled={false}
                                    date={reportDateTo}
                                />
                            )}
                        </DialogContent>
                    </Dialog>
                </CardContent>
            </Card>
        </MDBox>
    );
}
